import React, { useRef, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

const MAX_MEDIA_FILE_SIZE = 150 * 1024 * 1024;

const FileUpload = ({ orderId, onUploadSuccess, onUploadError, uploadEndpoint = 'upload-packaging-file', fieldType = 'packaging' }) => {
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    
    // Фильтруем только изображения и видео
    const mediaFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const extension = file.name.split('.').pop()?.toLowerCase();
      const knownMediaExtension = [
        'jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif',
        'mp4', 'mov', 'm4v', 'avi', 'webm', '3gp', 'mkv'
      ].includes(extension);
      return isImage || isVideo || knownMediaExtension;
    });

    const validFiles = mediaFiles.filter(file => file.size <= MAX_MEDIA_FILE_SIZE);
    const invalidTypeCount = files.length - mediaFiles.length;
    const oversizedFiles = mediaFiles.filter(file => file.size > MAX_MEDIA_FILE_SIZE);

    if (invalidTypeCount > 0) {
      onUploadError?.('Можно загружать только изображения и видеофайлы.');
    } else if (oversizedFiles.length > 0) {
      onUploadError?.(`Файл «${oversizedFiles[0].name}» больше 150 МБ. Уменьшите видео и повторите загрузку.`);
    }

    setSelectedFiles(validFiles);
    
    // Сбрасываем прогресс для новых файлов
    const newProgress = {};
    validFiles.forEach((file, index) => {
      newProgress[index] = 0;
    });
    setUploadProgress(newProgress);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      alert('Выберите файлы для загрузки');
      return;
    }

    setUploading(true);
    const failedFiles = [];
    const failedReasons = [];
    let uploadedCount = 0;

    try {
      const token = localStorage.getItem('token');
      
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('orderId', orderId);
        formData.append('fieldType', fieldType);

        try {
          console.log(`Загрузка файла ${file.name} через ${uploadEndpoint} с fieldType: ${fieldType}, orderId: ${orderId}`);
          
          const response = await axios.post(
            `${API_URL}/api/${uploadEndpoint}`,
            formData,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              },
              timeout: 10 * 60 * 1000,
              onUploadProgress: (progressEvent) => {
                if (!progressEvent.total) return;
                const progress = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
                setUploadProgress(prev => ({
                  ...prev,
                  [i]: progress
                }));
              }
            }
          );

          if (response.data.success) {
            uploadedCount += 1;
            console.log(`Файл ${file.name} успешно загружен через ${uploadEndpoint}`);
            console.log('Ответ сервера:', response.data);
          } else {
            throw new Error(response.data.message || 'неизвестная ошибка');
          }
        } catch (fileError) {
          failedFiles.push(file);
          let reason = fileError.response?.data?.message || fileError.response?.data?.error || fileError.message;
          if (fileError.response?.status === 413) {
            reason = 'файл превышает допустимый размер 150 МБ';
          } else if (fileError.code === 'ECONNABORTED') {
            reason = 'истекло время ожидания; проверьте интернет и повторите загрузку';
          } else if (!fileError.response && !navigator.onLine) {
            reason = 'нет подключения к интернету';
          }
          failedReasons.push(`${file.name}: ${reason || 'неизвестная ошибка'}`);
          console.error(`Ошибка загрузки файла ${file.name}:`, fileError);
          console.error('Детали ошибки:', {
            message: fileError.message,
            response: fileError.response?.data,
            status: fileError.response?.status,
            uploadEndpoint,
            fieldType,
            orderId
          });
        }
      }

      // Убираем только успешно загруженные файлы. Ошибочные можно повторить.
      setSelectedFiles(failedFiles);
      setUploadProgress({});
      if (failedFiles.length === 0 && fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      if (failedFiles.length > 0) {
        onUploadError?.(
          `Не удалось загрузить ${failedFiles.length} из ${selectedFiles.length} файлов. ${failedReasons[0]}. Повторите загрузку.`
        );
      } else if (uploadedCount > 0) {
        onUploadSuccess?.('Все файлы успешно загружены');
      }

    } catch (error) {
      console.error('Общая ошибка загрузки:', error);
      if (onUploadError) {
        onUploadError(`Ошибка загрузки: ${error.message}`);
      }
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    
    const newProgress = { ...uploadProgress };
    delete newProgress[index];
    
    // Переиндексируем прогресс
    const reindexedProgress = {};
    Object.keys(newProgress).forEach((key, newIndex) => {
      if (parseInt(key) > index) {
        reindexedProgress[newIndex] = newProgress[key];
      } else if (parseInt(key) < index) {
        reindexedProgress[key] = newProgress[key];
      }
    });
    
    setUploadProgress(reindexedProgress);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getUploadTitle = () => {
    switch (fieldType) {
      case 'storage':
        return 'Загрузка фото и видео склада';
      case 'loft':
        return 'Загрузка фото и видео лофта';
      case 'packaging':
      default:
        return 'Загрузка фото и видео ';
    }
  };

  return (
    <div className="bg-white border border-gray-300 rounded-lg p-4 mt-4">
      <h3 className="text-lg font-medium mb-4">{getUploadTitle()}</h3>
      
      {/* Область выбора файлов */}
      <div className="mb-4">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*"
          onChange={handleFileSelect}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          disabled={uploading}
        />
        <p className="text-xs text-gray-500 mt-1">
          Поддерживаются фото и видео до 150 МБ: JPG, PNG, HEIC, MP4, AVI, MOV и другие
        </p>
      </div>

      {/* Список выбранных файлов */}
      {selectedFiles.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium mb-2">Выбранные файлы:</h4>
          <div className="space-y-2">
            {selectedFiles.map((file, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                <div className="flex items-center space-x-2">
                  <div className="text-2xl">
                    {file.type.startsWith('image/') ? '📷' : '🎥'}
                  </div>
                  <div>
                    <div className="text-sm font-medium">{file.name}</div>
                    <div className="text-xs text-gray-500">{formatFileSize(file.size)}</div>
                  </div>
                </div>
                
                {/* Прогресс загрузки */}
                {uploading && uploadProgress[index] !== undefined && (
                  <div className="flex items-center space-x-2">
                    <div className="w-20 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${uploadProgress[index]}%` }}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-600">{uploadProgress[index]}%</span>
                  </div>
                )}
                
                {/* Кнопка удаления */}
                {!uploading && (
                  <button
                    onClick={() => removeFile(index)}
                    className="text-red-500 hover:text-red-700 text-sm font-medium"
                  >
                    Удалить
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Кнопка загрузки */}
      <button
        onClick={handleUpload}
        disabled={uploading || selectedFiles.length === 0}
        className={`w-full py-2 px-4 rounded-md font-medium transition-colors ${
          uploading || selectedFiles.length === 0
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-green-600 text-white hover:bg-green-700'
        }`}
      >
        {uploading ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            <span>Загрузка файлов...</span>
          </div>
        ) : (
          `Загрузить файлы (${selectedFiles.length})`
        )}
      </button>
    </div>
  );
};

export default FileUpload;
